import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { FotoIntervento } from 'api'
import { CircleAlertIcon, SearchIcon, WrenchIcon } from 'lucide-react'

import { dayjs } from '@/lib/dayjs'
import { useDexieQuery } from '@/hooks/use-dexie-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Breadcrumbs } from '@/components/breadcrumbs'
import { PageTitle } from '@/components/layout/page-title'
import { useDexieDb } from '@/db/dexie-context'
import type { InterventoDoc } from '@/db/local/dexie-db'
import { useFotoUrl } from '@/features/foto-interventi/use-foto-url'
import { InterventoStatoBadge } from '@/features/interventi/components/stato-badge'
import { statoOfDoc } from '@/features/interventi/eventi'
import {
  InRepairBy,
  StartRepairButton,
} from '@/features/riparazioni/components/repair-section'
import { StoricoTimeline } from '@/features/riparazioni/components/storico-timeline'
import {
  lookupForRepair,
  normalizeId,
  type LookupOutcome,
} from '@/features/riparazioni/lookup'
import type { Who } from '@/features/riparazioni/record-repair'
import { repairOptions } from '@/features/riparazioni/repair-options'
import { useWho } from '@/features/riparazioni/use-who'
import { useOperatoreNames } from '@/features/utenti/use-operatore-names'
import { interventoSearchSchema } from '@/routes/_authenticated/interventi/-intervento-search-schema'

export const Route = createFileRoute('/_authenticated/interventi/riparazione')({
  component: RouteComponent,
  validateSearch: interventoSearchSchema,
})

const messages: Record<Exclude<LookupOutcome['kind'], 'found'>, string> = {
  not_found: 'Nessuna caditoia da riparare con questo ID',
  offline: 'Connettiti a internet per cercare l’intervento',
  unavailable:
    'La ricerca non è disponibile in questo momento: riprova più tardi',
  failed: 'Ricerca non riuscita: controlla la connessione e riprova',
}

/** Finds a drain by the ID written on it, to repair it. */
function RouteComponent() {
  const db = useDexieDb()
  const who = useWho()
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [outcome, setOutcome] = useState<LookupOutcome>()

  const search = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!query.trim()) return
    setSearching(true)
    try {
      setOutcome(await lookupForRepair(db, query))
    } finally {
      setSearching(false)
    }
  }

  const ids = outcome?.kind === 'found' ? outcome.ids : []
  const found = useDexieQuery(
    async (database) =>
      (await database.interventi.bulkGet(ids)).filter(
        (row): row is InterventoDoc => !!row && !row.deleted_at
      ),
    [ids.join(',')]
  )

  return (
    <>
      <Breadcrumbs
        items={[
          { name: 'Interventi', to: '/interventi' },
          { name: 'Riparazione', to: '/interventi/riparazione' },
        ]}
      />

      <div className="max-w-xl space-y-6">
        <PageTitle>Riparazione</PageTitle>

        <form onSubmit={search} className="space-y-2">
          <Label htmlFor="id-caditoia">ID caditoia</Label>
          <div className="flex gap-2">
            <Input
              id="id-caditoia"
              placeholder="es. MR7KQ2"
              value={query}
              // IDs are uppercase letters and digits: written that way as
              // typed, whatever the keyboard sends
              onChange={(e) => setQuery(normalizeId(e.target.value))}
              autoCapitalize="characters"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            <Button type="submit" disabled={searching || !query.trim()}>
              <SearchIcon data-icon="inline-start" />
              Cerca
            </Button>
          </div>
        </form>

        {outcome && outcome.kind !== 'found' && (
          <p className="flex items-center gap-2 text-sm text-slate-700">
            <CircleAlertIcon className="size-4 shrink-0 text-slate-500" />
            {messages[outcome.kind]}
          </p>
        )}

        {outcome?.kind === 'found' && found && found.length > 1 && (
          <p className="text-sm text-slate-700">
            Più interventi con questo ID, scegli quello giusto
          </p>
        )}

        {outcome?.kind === 'found' && outcome.fromDevice && (
          <p className="text-xs text-slate-500">
            Risultato dal telefono: senza connessione potrebbe non essere
            aggiornato.
          </p>
        )}

        {who &&
          found?.map((intervento) => (
            <LookupCard key={intervento.id} intervento={intervento} who={who} />
          ))}
      </div>
    </>
  )
}

/** An intervento found, with what the user can do: start, continue, or nothing. */
function LookupCard({
  intervento,
  who,
}: {
  intervento: InterventoDoc
  who: Who
}) {
  const navigate = useNavigate()
  const names = useOperatoreNames()
  const fotos = useDexieQuery(
    (database) =>
      database.foto_interventi
        .where({ intervento_id: intervento.id })
        .filter((foto) => !foto.deleted_at)
        .toArray(),
    [intervento.id]
  )
  const options = repairOptions(intervento, who)
  const inspectionPhotos = (fotos ?? []).filter(
    (foto) => foto.riparazione_id == null
  )
  const stato = statoOfDoc(intervento)
  const open = () =>
    navigate({
      to: '/interventi/modifica/$interventoId',
      params: { interventoId: intervento.id },
    })

  return (
    <article className="space-y-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-base font-semibold text-slate-900">
            {intervento.id_interno}
          </p>
          <p className="text-sm text-slate-600">{intervento.indirizzo}</p>
          {intervento.data && (
            <p className="text-xs text-slate-500">
              Ispezione del {dayjs(intervento.data).format('DD/MM/YYYY')}
            </p>
          )}
        </div>
        <InterventoStatoBadge
          intervento={intervento}
          className="rounded-full px-3 py-1 text-sm font-semibold"
        />
      </header>

      {options.others.map((phase) => (
        <InRepairBy key={phase.repairId} repair={phase} names={names} />
      ))}

      {(stato === 'positivo' || stato === 'riparato') && (
        <p className="text-sm text-slate-700">
          Questa caditoia risulta già riparata
        </p>
      )}
      {(stato === 'bozza' || stato === 'in_corso') && (
        <p className="text-sm text-slate-700">
          L’ispezione non è ancora completata
        </p>
      )}

      {/* The inspection's photos: what the failure looked like */}
      {inspectionPhotos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {inspectionPhotos.map((foto) => (
            <Thumbnail key={foto.id} foto={foto} />
          ))}
        </div>
      )}

      <StoricoTimeline intervento={intervento} fotos={fotos} names={names} />

      <div className="flex flex-wrap gap-2">
        {options.mine && (
          <Button type="button" onClick={open}>
            <WrenchIcon data-icon="inline-start" />
            Continua riparazione
          </Button>
        )}
        {options.canStart && (
          <StartRepairButton
            intervento={intervento}
            names={names}
            who={who}
            options={options}
            onStarted={open}
          />
        )}
      </div>
    </article>
  )
}

/** A small photo, opened full size in a new tab. */
function Thumbnail({ foto }: { foto: FotoIntervento & { blob?: File } }) {
  const url = useFotoUrl(foto)
  const label = `Foto ${(foto.categoria ?? '').replace('_', ' ')}`
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      className="size-16 overflow-hidden rounded-md border border-slate-200 bg-slate-50"
    >
      {url && (
        <img src={url} alt={label} className="h-full w-full object-cover" />
      )}
    </a>
  )
}
